<template>
  <div class="document-segment-mapper">
    <div class="mapping-panel">
      <div class="mapping-header">
        <div class="mapping-title-area">
          <h3 class="mapping-title">Assign Media to Segments</h3>
          <span v-if="mappedSegmentsCount > 0" class="mapping-count">({{ mappedSegmentsCount }})</span>
        </div>
        <button class="toggle-btn" @click="toggleMappingPanel">
          {{ mappingPanelExpanded ? '▼' : '▶' }}
        </button>
      </div>

      <div v-show="mappingPanelExpanded" class="mapping-content">
        <div class="instructions">
          <p>Assign media to specific segments of your conversation
          </p>
        </div>

        <!-- NEW: Filter Controls for Segments -->
        <div v-if="segments.length > 0" class="segment-filter-controls">
          <div class="search-box">
            <input
              type="text"
              placeholder="Search segments..."
              class="search-input"
              v-model="segmentSearchQuery"
              @input="filterSegments"
            >
            <svg class="search-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </div>
          <div class="segment-filter-buttons">
            <button
              class="filter-btn"
              :class="{ active: segmentFilterType === 'all' }"
              @click="setSegmentFilter('all')"
            >
              All ({{ segments.length }})
            </button>
            <button
              class="filter-btn"
              :class="{ active: segmentFilterType === 'mapped' }"
              @click="setSegmentFilter('mapped')"
              v-if="mappedSegmentsCount > 0"
            >
              Mapped ({{ mappedSegmentsCount }})
            </button>
            <button
              class="filter-btn"
              :class="{ active: segmentFilterType === 'unmapped' }"
              @click="setSegmentFilter('unmapped')"
              v-if="segments.length - mappedSegmentsCount > 0"
            >
              Unmapped ({{ segments.length - mappedSegmentsCount }})
            </button>
          </div>
          <div class="sort-controls">
            <label for="segment-sort-select" class="sort-label">Sort:</label>
            <select id="segment-sort-select" v-model="segmentSortBy" class="sort-select" @change="$forceUpdate()">
              <option value="index">Position</option>
              <option value="speaker">Speaker</option>
              <option value="length">Length</option>
            </select>
          </div>
        </div>

        <!-- Segment List with Filtered Results -->
        <div class="segment-list">
          <div
            v-for="(segment, index) in displayedSegments"
            :key="`segment-${index}`"
            class="segment-item"
            :class="{ 'active': selectedSegmentIndex === segment.originalIndex }"
            @click="selectSegment(segment.originalIndex)"
            :title="segment.content"
          >
            <div class="segment-info">
              <span class="segment-number">{{ segment.originalIndex + 1 }}</span>
              <span class="segment-speaker">{{ segment.speaker }}</span>
              <span class="segment-preview">{{ truncateText(segment.content, 60) }}</span>
            </div>
            <div class="document-badges">
              <span
                v-if="getDocumentsForSegment(segment.originalIndex).length > 0"
                class="document-counter"
              >
                {{ getDocumentsForSegment(segment.originalIndex).length }}
              </span>
            </div>
          </div>
        </div>

        <!-- No Segments Results Message -->
        <div v-if="segments.length > 0 && displayedSegments.length === 0" class="no-results">
          <p>No segments found matching your criteria.</p>
          <button class="reset-filter-btn" @click="resetSegmentFilters">Reset filters</button>
        </div>

        <!-- Document Selector for Selected Segment -->
        <div v-if="selectedSegmentIndex !== null" class="document-selector">
          <div class="selector-header">
            <h4>Documents for Segment {{ selectedSegmentIndex + 1 }}</h4>
            <!-- Add clear selection button -->
            <button
              v-if="getDocumentsForSegment(selectedSegmentIndex).length > 0"
              class="clear-selection-btn"
              @click="clearSegmentSelection(selectedSegmentIndex)"
              title="Clear selection for this segment"
            >
              Clear Selection
            </button>
          </div>

          <!-- NEW: Filter Controls for Documents -->
          <div v-if="allDocuments.length > 0" class="document-filter-controls">
            <div class="filter-title">Filter by:</div>
            <div class="filter-buttons">
              <button
                class="filter-btn"
                :class="{ active: documentFilterType === 'all' }"
                @click="setDocumentFilter('all')"
              >
                All ({{ allDocuments.length }})
              </button>
              <button
                class="filter-btn"
                :class="{ active: documentFilterType === 'image' }"
                @click="setDocumentFilter('image')"
                v-if="documentCounts.image > 0"
              >
                Images ({{ documentCounts.image }})
              </button>
              <button
                class="filter-btn"
                :class="{ active: documentFilterType === 'video' }"
                @click="setDocumentFilter('video')"
                v-if="documentCounts.video > 0"
              >
                Videos ({{ documentCounts.video }})
              </button>
              <button
                class="filter-btn"
                :class="{ active: documentFilterType === 'document' }"
                @click="setDocumentFilter('document')"
                v-if="documentCounts.document > 0"
              >
                Documents ({{ documentCounts.document }})
              </button>
            </div>
            <div class="sort-controls">
              <label for="document-sort-select" class="sort-label">Sort:</label>
              <select id="document-sort-select" v-model="documentSortBy" class="sort-select">
                <option value="newest">Newest</option>
                <option value="oldest">Oldest</option>
                <option value="name">Name</option>
              </select>
            </div>
            <div class="search-box">
              <input
                type="text"
                placeholder="Search documents..."
                class="search-input"
                v-model="documentSearchQuery"
                @input="filterDocuments"
              >
              <svg class="search-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </div>
          </div>

          <!-- Empty state message when no documents -->
          <div v-if="allDocuments.length === 0" class="empty-documents-message">
            No media uploaded yet. Please upload media files first using the "Upload Media" button above.
          </div>

          <!-- Carousel Navigation Arrows with Filtered Documents -->
          <div v-else class="carousel-container">
            <div class="scroll-arrow left" @click.stop="scrollCarousel('left')" v-if="showScrollButtons && hasScrollLeft">
              <span>◀</span>
            </div>

            <!-- Carousel Track with Filtered Documents -->
            <div class="carousel-track" ref="carouselTrack" @scroll="handleScroll">
              <div
                v-for="doc in displayedDocuments"
                :key="doc.uniqueKey"
                class="carousel-item"
                :class="{ 'mapped': isDocumentMappedToSegment(doc.uniqueKey, selectedSegmentIndex) }"
                @click="toggleDocumentForSegment(doc.uniqueKey, selectedSegmentIndex)"
                :data-doc-key="doc.uniqueKey"
              >
                <div class="upload-card">
                  <div class="upload-image">
                    <img v-if="isImageFile(doc.type)"
                        :src="doc.dataUrl || doc.preview"
                        :alt="doc.name"
                        @error="handleImageError">

                    <!-- Video thumbnail -->
                    <div v-else-if="isVideoFile(doc.type)" class="video-thumbnail">
                      <div class="video-icon">▶</div>
                      <img v-if="doc.preview"
                           :src="doc.preview"
                           :alt="doc.name"
                           @error="handleImageError">
                      <div v-else class="video-placeholder">
                        MP4
                      </div>
                    </div>

                    <div v-else class="file-icon" :class="getFileIconClass(doc.type)">
                      {{ getFileExtension(doc.name).toUpperCase() }}
                    </div>
                  </div>
                  <div class="upload-info">
                    <div class="upload-title">{{ truncateFileName(doc.name) }}</div>
                    <div class="upload-meta">
                      {{ formatFileSize(doc.size) }} · {{ formatFileType(doc.type) }}
                    </div>
                  </div>
                  <!-- Media type badges - NEW -->
                  <div
                    v-if="isImageFile(doc.type)"
                    class="media-type-badge image-badge"
                  >
                    Image
                  </div>
                  <div
                    v-if="isVideoFile(doc.type)"
                    class="media-type-badge video-badge"
                  >
                    Video
                  </div>
                  <div
                    v-if="!isImageFile(doc.type) && !isVideoFile(doc.type)"
                    class="media-type-badge doc-badge"
                  >
                    Doc
                  </div>
                  <div class="mapping-indicator" :class="{ 'is-mapped': isDocumentMappedToSegment(doc.uniqueKey, selectedSegmentIndex) }">
                    {{ isDocumentMappedToSegment(doc.uniqueKey, selectedSegmentIndex) ? '✓' : '+' }}
                  </div>
                  <!-- Tooltip for card action -->
                  <div class="card-tooltip">
                    {{ isDocumentMappedToSegment(doc.uniqueKey, selectedSegmentIndex) ? 'Click to unassign' : 'Click to assign' }}
                  </div>
                </div>
              </div>
            </div>

            <div class="scroll-arrow right" @click.stop="scrollCarousel('right')" v-if="showScrollButtons && hasScrollRight">
              <span>▶</span>
            </div>
          </div>

          <!-- No Documents Results Message -->
          <div v-if="allDocuments.length > 0 && displayedDocuments.length === 0" class="no-results">
            <p>No documents found matching your criteria.</p>
            <button class="reset-filter-btn" @click="resetDocumentFilters">Reset filters</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Preview Modal -->
    <div v-if="previewOpen" class="preview-modal" @click="closePreview">
      <div class="preview-content" @click.stop>
        <div class="preview-header">
          <h3>{{ activeDoc ? activeDoc.name : '' }}</h3>
          <button class="close-preview" @click="closePreview">×</button>
        </div>
        <div class="preview-body">
          <!-- Image preview -->
          <img v-if="activeDoc && isImageFile(activeDoc.type)"
               :src="activeDoc.dataUrl || activeDoc.preview"
               :alt="activeDoc.name">

          <!-- Video preview -->
          <video v-else-if="activeDoc && isVideoFile(activeDoc.type)"
                 controls
                 autoplay
                 muted
                 preload="metadata"
                 loop
                 class="preview-video"
                 :src="activeDoc.dataUrl"
                 :alt="activeDoc.name"
                 @error="handleMediaError"></video>

          <div v-else class="preview-placeholder">
            <div class="file-icon large" :class="getFileIconClass(activeDoc ? activeDoc.type : '')">
              {{ getFileExtension(activeDoc ? activeDoc.name : '').toUpperCase() }}
            </div>
            <p>Preview not available</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import API from '@/api';
import ProFeatureChip from '../components/ProFeatureChip.vue';
import { mapActions } from 'vuex';

export default {
  name: 'DocumentSegmentMapper',
  components: {
    ProFeatureChip
  },
  props: {
    isMobile: {
      type: Boolean,
      default: false
    },
    isPro: {
      type: Boolean,
      default: false
    },
    messageId: {
      required: true
    },
    segments: {
      type: Array,
      required: true,
      default: () => []
    },
    activeSegmentIndex: {
      type: Number,
      default: null
    },
    lastDocumentUpdate: {
      type: Number,
      default: 0
    },
    uploadedDocuments: {
      type: Object,
      default: () => ({})
    },
    documentData: {
      type: Object,
      default: () => ({})
    },
    showMappingPanel: {
      type: Boolean,
      default: false
    },
    // Add this prop to directly pass segment mappings
    segmentMappings: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      mappingPanelExpanded: false,
      selectedSegmentIndex: null,
      documentSegmentMap: {}, // Format: { segmentIndex: ['uniqueKey1', 'uniqueKey2', ...] }

      // Carousel data
      showScrollButtons: true,
      hasScrollLeft: false,
      hasScrollRight: true,

      // Preview data
      previewOpen: false,
      activeDoc: null,

      // Segment filtering
      segmentSearchQuery: '',
      segmentFilterType: 'all',
      segmentSortBy: 'index',
      debouncedSegmentSearchTimeout: null,
      processedSegments: [], // Will hold segments with additional metadata

      // Document filtering
      documentSearchQuery: '',
      documentFilterType: 'all',
      documentSortBy: 'newest',
      debouncedDocumentSearchTimeout: null
    };
  },
  computed: {
    documentMapKey() {
      return `doc-segment-map-${this.messageId}`;
    },

    allDocuments() {
      // Add a dependency on lastDocumentUpdate to force recalculation
      const updateTimestamp = this.lastDocumentUpdate;

      // Build a combined collection from both sources
      const documents = [];
      const processedKeys = new Set();

      // First process documentData (priority source)
      if (this.documentData) {
        Object.keys(this.documentData).forEach(messageId => {
          const messageDocs = this.documentData[messageId] || {};

          Object.keys(messageDocs).forEach(docKey => {
            const doc = messageDocs[docKey];
            if (doc) {
              // Make sure we have the full data structure needed
              documents.push({
                ...doc,
                uniqueKey: docKey,
                messageId,
                dataUrl: doc.preview || doc.dataUrl, // Ensure dataUrl is available
                preview: doc.preview || doc.dataUrl,
                timestamp: doc.timestamp || new Date().toISOString()
              });
              processedKeys.add(docKey);
            }
          });
        });
      }

      return documents;
    },

    mappedSegmentsCount() {
      // Count how many segments have mapped documents
      return Object.keys(this.documentSegmentMap).filter(key =>
        this.documentSegmentMap[key] && this.documentSegmentMap[key].length > 0
      ).length;
    },

    // Document filtering computed properties
    documentCounts() {
      const counts = {
        all: this.allDocuments.length,
        image: 0,
        video: 0,
        document: 0
      };

      this.allDocuments.forEach(doc => {
        if (this.isImageFile(doc.type)) {
          counts.image++;
        } else if (this.isVideoFile(doc.type)) {
          counts.video++;
        } else {
          counts.document++;
        }
      });

      return counts;
    },

    displayedDocuments() {
      // Start with all documents
      let filtered = [...this.allDocuments];

      // Apply type filter
      if (this.documentFilterType !== 'all') {
        filtered = filtered.filter(doc => {
          if (this.documentFilterType === 'image') {
            return this.isImageFile(doc.type);
          } else if (this.documentFilterType === 'video') {
            return this.isVideoFile(doc.type);
          } else if (this.documentFilterType === 'document') {
            return !this.isImageFile(doc.type) && !this.isVideoFile(doc.type);
          }
          return true;
        });
      }

      // Apply search query filter if it exists
      if (this.documentSearchQuery && this.documentSearchQuery.trim() !== '') {
        const query = this.documentSearchQuery.toLowerCase().trim();
        filtered = filtered.filter(doc => {
          // Search in filename
          return doc.name.toLowerCase().includes(query);
        });
      }

      // Apply sorting
      if (this.documentSortBy === 'newest') {
        filtered.sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
      } else if (this.documentSortBy === 'oldest') {
        filtered.sort((a, b) => new Date(a.timestamp || 0) - new Date(b.timestamp || 0));
      } else if (this.documentSortBy === 'name') {
        filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      }

      return filtered;
    },

    // Segment filtering computed properties
    displayedSegments() {
      // Start with processed segments
      let filtered = [...this.processedSegments];

      // Apply filter type
      if (this.segmentFilterType === 'mapped') {
        filtered = filtered.filter(segment =>
          this.getDocumentsForSegment(segment.originalIndex).length > 0
        );
      } else if (this.segmentFilterType === 'unmapped') {
        filtered = filtered.filter(segment =>
          this.getDocumentsForSegment(segment.originalIndex).length === 0
        );
      }

      // Apply search query
      if (this.segmentSearchQuery && this.segmentSearchQuery.trim() !== '') {
        const query = this.segmentSearchQuery.toLowerCase().trim();
        filtered = filtered.filter(segment => {
          return (
            (segment.content || '').toLowerCase().includes(query) ||
            (segment.speaker || '').toLowerCase().includes(query)
          );
        });
      }

      // Apply sorting
      if (this.segmentSortBy === 'index') {
        filtered.sort((a, b) => a.originalIndex - b.originalIndex);
      } else if (this.segmentSortBy === 'speaker') {
        filtered.sort((a, b) => (a.speaker || '').localeCompare(b.speaker || ''));
      } else if (this.segmentSortBy === 'length') {
        filtered.sort((a, b) => (b.content ? b.content.length : 0) - (a.content ? a.content.length : 0));
      }

      return filtered;
    }
  },
  watch: {
    documentData: {
      handler(newVal) {
        this.refreshDocumentData();
      },
      deep: true
    },

    lastDocumentUpdate: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          // refresh the document data
          this.refreshDocumentData();
          // update documentsegmentMap
          this.documentSegmentMap = JSON.parse(JSON.stringify(this.segmentMappings));
          // Force computed properties to re-evaluate
          this.$forceUpdate();
          // Refresh the document display
          this.$nextTick(() => {
            if (this.$refs.carouselTrack) {
              this.handleScroll();

              // If a segment is selected and we have documents, scroll to the last one
              if (this.selectedSegmentIndex !== null && this.allDocuments.length > 0) {
                // Get the last document (most recently uploaded)
                const lastDoc = this.allDocuments[this.allDocuments.length - 1];
                if (lastDoc && lastDoc.uniqueKey) {
                  this.scrollToDocument(lastDoc.uniqueKey);
                }
              }
            }
          });
        }
      }
    },

    activeSegmentIndex(newVal, oldVal) {
      if (newVal !== null && newVal !== oldVal) {
        // Check if this segment has documents
        const hasDocuments = this.getDocumentsForSegment(newVal).length > 0;

        // If it has documents, emit an event
        if (hasDocuments) {
          this.$emit('segment-has-documents', {
            segmentIndex: newVal,
            documentCount: this.getDocumentsForSegment(newVal).length,
            documents: this.getDocumentsForSegment(newVal)
          });
        }
      }
    },

    selectedSegmentIndex() {
      // Reset scroll position when selected segment changes
      this.$nextTick(() => {
        if (this.$refs.carouselTrack) {
          // Check if there's a selected document for this segment and scroll to it
          const mappedDocs = this.getDocumentsForSegment(this.selectedSegmentIndex);
          if (mappedDocs.length > 0) {
            const mappedKey = mappedDocs[0].uniqueKey;
            this.scrollToDocument(mappedKey);
          } else {
            // Otherwise reset to start
            this.$refs.carouselTrack.scrollLeft = 0;
            this.handleScroll();
          }
        }
      });
    },

    // Watch for segments change to update processedSegments
    segments: {
      handler(newVal) {
        this.processSegments();
      },
      deep: true,
      immediate: true
    },

    // Use mappings from props
    segmentMappings: {
      handler(newMappings) {
        if (newMappings && Object.keys(newMappings).length > 0) {
          // Update our internal documentSegmentMap with the provided mappings
          this.documentSegmentMap = JSON.parse(JSON.stringify(newMappings));
        }
      },
      deep: true,
      immediate: true
    },

    // Watch for segmentSortBy changes to ensure sorting is applied
    segmentSortBy() {
      this.$nextTick(() => {
        this.$forceUpdate(); // Force re-render to apply sorting
      });
    },

    // Watch for documentSortBy changes to ensure sorting is applied
    documentSortBy() {
      this.$nextTick(() => {
        this.$forceUpdate(); // Force re-render to apply sorting
      });
    }
  },
  methods: {
    ...mapActions('websocket', ['showTempMessageFn']),

    // Process segments for filtering
    processSegments() {
      this.processedSegments = this.segments.map((segment, index) => ({
        ...segment,
        originalIndex: index
      }));
    },

    // Segment filtering methods
    filterSegments() {
      // Clear any existing timeout to debounce rapid typing
      if (this.debouncedSegmentSearchTimeout) {
        clearTimeout(this.debouncedSegmentSearchTimeout);
      }

      // Set a timeout to avoid filtering on each keystroke
      this.debouncedSegmentSearchTimeout = setTimeout(() => {
        // The actual filtering is handled by the displayedSegments computed property
        // We just need to trigger its recalculation
        this.$forceUpdate();
      }, 300);
    },

    setSegmentFilter(filter) {
      this.segmentFilterType = filter;
      this.$forceUpdate(); // Force computed properties to recalculate
    },

    resetSegmentFilters() {
      this.segmentSearchQuery = '';
      this.segmentFilterType = 'all';
      this.segmentSortBy = 'index';
      this.$forceUpdate(); // Force computed properties to recalculate
    },

    // Document filtering methods
    filterDocuments() {
      // Clear any existing timeout to debounce rapid typing
      if (this.debouncedDocumentSearchTimeout) {
        clearTimeout(this.debouncedDocumentSearchTimeout);
      }

      // Set a timeout to avoid filtering on each keystroke
      this.debouncedDocumentSearchTimeout = setTimeout(() => {
        // The actual filtering is handled by the displayedDocuments computed property
        // We just need to trigger its recalculation
        this.$forceUpdate();
      }, 300);
    },

    setDocumentFilter(filter) {
      this.documentFilterType = filter;
      this.$forceUpdate(); // Force computed properties to recalculate
    },

    resetDocumentFilters() {
      this.documentSearchQuery = '';
      this.documentFilterType = 'all';
      this.documentSortBy = 'newest';
      this.$forceUpdate(); // Force computed properties to recalculate
    },

    scrollToDocument(docKey) {
      if (!this.$refs.carouselTrack) return;

      this.$nextTick(() => {
        const track = this.$refs.carouselTrack;
        const docElement = track.querySelector(`[data-doc-key="${docKey}"]`);

        if (docElement) {
          // Calculate position to center the element in the view
          const trackWidth = track.clientWidth;
          const elementLeft = docElement.offsetLeft;
          const elementWidth = docElement.offsetWidth;
          const scrollPosition = elementLeft - (trackWidth - elementWidth) / 2;

          track.scrollLeft = Math.max(0, scrollPosition);

          // Add a brief highlight effect to the newly scrolled-to document
          docElement.classList.add('highlight-new');
          setTimeout(() => {
            docElement.classList.remove('highlight-new');
          }, 3500);
        }
      });
    },

    async updateMappingsForSegmentChange(operation, segmentIndex, newSegmentCount) {
      // Make a deep copy of current mappings
      const oldMap = JSON.parse(JSON.stringify(this.documentSegmentMap));
      const newMap = {};

      if (operation === 'delete') {
        Object.keys(oldMap).forEach(key => {
          const index = parseInt(key);
          if (index < segmentIndex) {
            newMap[index] = oldMap[index];
          } else if (index > segmentIndex) {
            newMap[index - 1] = oldMap[index];
          }
        });
      } else if (operation === 'add') {
        Object.keys(oldMap).forEach(key => {
          const index = parseInt(key);
          if (index < segmentIndex) {
            newMap[index] = oldMap[index];
          } else {
            newMap[index + 1] = oldMap[index];
          }
        });
      }

      // Update the mapping
      this.documentSegmentMap = newMap;

      // Save the new mapping to backend
      await this.saveAllMappings();
    },

    async saveAllMappings() {
      try {
        const mappingData = {};

        // Keep the unique keys as they are - don't convert to IDs
        Object.keys(this.documentSegmentMap).forEach(segmentIndex => {
          // Just copy the array of unique keys directly
          mappingData[segmentIndex] = [...this.documentSegmentMap[segmentIndex]];
        });

        // Save all mappings in one call
        await API.post(`/user_customization/messages/${this.messageId}/segments/mappings`, {
          mappings: mappingData
        });

        this.$emit('mappings-updated', this.documentSegmentMap);
      } catch (error) {
        console.error('Error saving segment mappings:', error);
        this.showTempMessageFn({
          message: 'Failed to update document mappings. Please try again.',
          type: 'error',
          timeout: 3000
        });
      }
    },

    refreshDocumentData() {
      // Force computed properties to re-evaluate
      this.$forceUpdate();

      // Schedule UI updates on next tick
      this.$nextTick(() => {
        if (this.$refs.carouselTrack) {
          this.handleScroll();
        }

        // If you have a selected segment, make sure its documents are updated
        if (this.selectedSegmentIndex !== null) {
          const docs = this.getDocumentsForSegment(this.selectedSegmentIndex);
        }
      });
    },

    toggleMappingPanel() {
      this.mappingPanelExpanded = !this.mappingPanelExpanded;
    },

    checkForEmptyDocuments() {
      if (this.allDocuments.length === 0 && this.selectedSegmentIndex !== null) {
        // Highlight the upload button by finding it in the DOM (add a temporary class)
        const uploadBtn = document.querySelector('.upload-button');
        if (uploadBtn) {
          uploadBtn.classList.add('highlight-upload');
          setTimeout(() => {
            uploadBtn.classList.remove('highlight-upload');
          }, 3000);
        }
      }
    },

    selectSegment(index) {
      this.selectedSegmentIndex = this.selectedSegmentIndex === index ? null : index;
      this.$emit('segment-selected', this.selectedSegmentIndex);

      // If no documents are available, show a message
      if (this.selectedSegmentIndex !== null) {
        this.$nextTick(() => {
          this.checkForEmptyDocuments();
        });
      }
    },

    getDocumentsForSegment(segmentIndex) {
      if (!this.documentSegmentMap[segmentIndex]) {
        return [];
      }

      // Get the document keys for this segment
      const docKeys = this.documentSegmentMap[segmentIndex];

      // Map the keys to actual document objects
      return docKeys.map(key => {
        // First check allDocuments for the most up-to-date info
        const docFromComputed = this.allDocuments.find(d => d.uniqueKey === key);
        if (docFromComputed) {
          return docFromComputed;
        }

        // Extract messageId from uniqueKey (assuming format: messageId_uploadId)
        const [messageId] = key.split('_');

        // Try to find in documentData
        const documentDocs = (this.documentData && this.documentData[messageId]) || {};
        const documentDoc = documentDocs[key];

        // Return the first available document
        const doc = documentDoc;

        // Ensure the document has a uniqueKey property
        if (doc && !doc.uniqueKey) {
          doc.uniqueKey = key;
        }

        return doc;
      }).filter(doc => doc); // Filter out any undefined documents
    },

    isDocumentMappedToSegment(docKey, segmentIndex) {
      return this.documentSegmentMap[segmentIndex] &&
               this.documentSegmentMap[segmentIndex].includes(docKey);
    },

    async toggleDocumentForSegment(docKey, segmentIndex) {
      // Initialize the array for this segment if it doesn't exist
      if (!this.documentSegmentMap[segmentIndex]) {
        this.$set(this.documentSegmentMap, segmentIndex, []);
      }

      const index = this.documentSegmentMap[segmentIndex].indexOf(docKey);
      const document = this.allDocuments.find(doc => doc.uniqueKey === docKey);

      if (!document) {
        console.error('Document not found:', docKey);
        return;
      }

      if (index === -1) {
        // CHANGED: Clear any existing selection for this segment (limit to one document)
        this.documentSegmentMap[segmentIndex] = [docKey];
      } else {
        // Remove the document from this segment
        this.documentSegmentMap[segmentIndex].splice(index, 1);
      }

      try {
        // Save the mapping to the backend.
        await API.post(`/user_customization/messages/${this.messageId}/segments/${segmentIndex}/mappings`, {
          document_ids: this.documentSegmentMap[segmentIndex].map(key => {
            // Find the document by uniqueKey and get its ID
            const doc = this.allDocuments.find(d => d.uniqueKey === key);
            return doc ? doc.id : null;
          }).filter(id => id !== null)
        });

        // Emit an event with the updated mapping
        this.$emit('mapping-updated', {
          segmentIndex,
          documentKeys: this.documentSegmentMap[segmentIndex],
          messageId: this.messageId,
          documentWithData: document
        });

        // Force displayedSegments to re-evaluate when mappings change
        this.$forceUpdate();
      } catch (error) {
        console.error('Error saving segment mapping:', error);

        // Show error message
        this.showTempMessageFn({
          message: 'Failed to save segment mapping. Please try again.',
          type: 'error',
          timeout: 3000
        });
      }
    },

    // Clear selection for a segment
    async clearSegmentSelection(segmentIndex) {
      if (!this.documentSegmentMap[segmentIndex] || this.documentSegmentMap[segmentIndex].length === 0) {
        return;
      }

      // Clear the mapping
      this.documentSegmentMap[segmentIndex] = [];

      try {
        // Save the empty mapping to the backend
        await API.post(`/user_customization/messages/${this.messageId}/segments/${segmentIndex}/mappings`, {
          document_ids: []
        });

        // Emit an event with the updated mapping
        this.$emit('mapping-updated', {
          segmentIndex,
          documentKeys: [],
          messageId: this.messageId,
          documentWithData: null
        });

        // Force displayedSegments to re-evaluate when mappings change
        this.$forceUpdate();
      } catch (error) {
        console.error('Error clearing segment mapping:', error);
        this.showTempMessageFn({
          message: 'Failed to clear segment mapping. Please try again.',
          type: 'error',
          timeout: 3000
        });
      }
    },

    // Carousel methods
    scrollCarousel(direction) {
      if (!this.$refs.carouselTrack) return;

      const scrollAmount = 300; // Adjust as needed
      const currentScroll = this.$refs.carouselTrack.scrollLeft;

      if (direction === 'left') {
        this.$refs.carouselTrack.scrollLeft = currentScroll - scrollAmount;
      } else {
        this.$refs.carouselTrack.scrollLeft = currentScroll + scrollAmount;
      }
    },

    handleScroll() {
      if (!this.$refs.carouselTrack) return;

      const track = this.$refs.carouselTrack;

      // Check if we can scroll left or right
      this.hasScrollLeft = track.scrollLeft > 0;
      this.hasScrollRight = track.scrollLeft < (track.scrollWidth - track.clientWidth);
    },

    // Preview methods
    openPreview(doc) {
      this.activeDoc = doc;
      this.previewOpen = true;

      // Prevent body scrolling
      document.body.style.overflow = 'hidden';
    },

    closePreview() {
      this.previewOpen = false;
      this.activeDoc = null;

      // Restore body scrolling
      document.body.style.overflow = '';
    },

    // Handle media error
    handleMediaError(e) {
      e.target.style.display = 'none';
      const errorMsg = document.createElement('div');
      errorMsg.className = 'media-error-message';
      errorMsg.textContent = `${e.target.tagName === 'VIDEO' ? 'Video' : 'Image'} could not be loaded`;
      e.target.parentNode.appendChild(errorMsg);
    },

    // Helper methods
    truncateText(text, maxLength) {
      if (!text) return '';

      // First strip HTML tags
      const plainText = text.replace(/<[^>]+>/g, '');

      if (plainText.length <= maxLength) return plainText;
      return plainText.substring(0, maxLength) + '...';
    },

    truncateFileName(name) {
      if (!name) return '';
      return name.length > 20 ? name.substring(0, 17) + '...' : name;
    },

    formatFileSize(size) {
      if (!size) return '';
      if (size < 1024) return size + ' B';
      if (size < 1024 * 1024) return Math.round(size / 1024) + ' KB';
      return Math.round(size / (1024 * 1024) * 10) / 10 + ' MB';
    },

    formatFileType(type) {
      if (!type) return 'File';
      return type.split('/').pop().toUpperCase();
    },

    isImageFile(fileType) {
      return fileType && fileType.startsWith('image/');
    },

    // Method for video files detection
    isVideoFile(fileType) {
      return fileType && (
        fileType.startsWith('video/') ||
        fileType.includes('mp4') ||
        fileType.includes('mov') ||
        fileType.includes('quicktime') ||
        fileType.includes('webm') ||
        fileType.includes('ogg')
      );
    },

    getFileExtension(filename) {
      if (!filename) return '';
      return filename.split('.').pop() || '';
    },

    getFileIconClass(fileType) {
      if (!fileType) return 'generic-icon';

      if (fileType.includes('pdf')) return 'pdf-icon';
      if (fileType.includes('word') || fileType.includes('doc')) return 'doc-icon';
      if (fileType.includes('excel') || fileType.includes('sheet') || fileType.includes('xls')) return 'xls-icon';
      if (fileType.includes('powerpoint') || fileType.includes('presentation') || fileType.includes('ppt')) return 'ppt-icon';
      if (this.isVideoFile(fileType)) return 'video-icon';

      return 'generic-icon';
    },

    handleImageError(e) {
      e.target.style.display = 'none';
      const placeholder = document.createElement('div');
      placeholder.className = 'file-icon generic-icon';
      placeholder.textContent = this.getFileExtension(e.target.alt).toUpperCase();
      e.target.parentNode.appendChild(placeholder);
    }
  },
  mounted() {
    // Initialize the mapping panel expanded state based on prop
    this.mappingPanelExpanded = this.showMappingPanel;

    // Initialize our documentSegmentMap from props if available
    if (this.segmentMappings && Object.keys(this.segmentMappings).length > 0) {
      this.documentSegmentMap = JSON.parse(JSON.stringify(this.segmentMappings));
    }

    this.$nextTick(() => {
      // Process segments for filtering
      this.processSegments();

      if (this.$refs.carouselTrack) {
        this.handleScroll();
      }

      // Add visual highlight for upload button after slight delay
      setTimeout(() => {
        const uploadBtn = document.querySelector('.upload-button');
        if (uploadBtn && this.allDocuments.length === 0) {
          uploadBtn.classList.add('highlight-upload');
          setTimeout(() => {
            uploadBtn.classList.remove('highlight-upload');
          }, 3000);
        }
      }, 2000);
    });
  }
}

</script>

<style scoped>
.document-segment-mapper {
  width: 100%;
  margin-bottom: 1rem;
}

/* Mapping Panel Styles */
.mapping-panel {
  background-color: #fff;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.theme--dark .mapping-panel {
  background-color: #1a202c;
}

/* Header Styles - Matching other components */
.mapping-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  cursor: pointer;
  border-bottom: 1px solid #e2e8f0;
  transition: all 0.3s ease;
}

.theme--dark .mapping-header {
  border-bottom-color: #2d3748;
}

.mapping-title-area {
  display: flex;
  align-items: center;
}

.mapping-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
}

.theme--dark .mapping-title {
  color: #e2e8f0;
}

.mapping-count {
  font-size: 0.75rem;
  color: #718096;
  margin-left: 0.25rem;
}

.theme--dark .mapping-count {
  color: #a0aec0;
}

.toggle-btn {
  padding: 0.25rem 0.375rem;
  font-size: 0.875rem;
  color: #718096;
  background: none;
  border: 1px solid #e2e8f0;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: all 0.15s ease;
}

.theme--dark .toggle-btn {
  border-color: #2d3748;
  color: #a0aec0;
}

.toggle-btn:hover {
  background-color: #f7fafc;
  color: #e53e3e;
  border-color: #e53e3e;
}

.theme--dark .toggle-btn:hover {
  background-color: #2d3748;
  color: #e53e3e;
  border-color: #e53e3e;
}

.mapping-content {
  padding: 1rem;
}

.instructions {
  margin-bottom: 1rem;
  font-size: 0.85rem;
  color: #4a5568;
}

.theme--dark .instructions {
  color: #a0aec0;
}

/* NEW - Filter Controls for Segments */
.segment-filter-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 1rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.theme--dark .segment-filter-controls {
  border-bottom-color: rgba(255, 255, 255, 0.05);
}

.segment-filter-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

/* Segment List Styles */
.segment-list {
  max-height: 250px;
  overflow-y: auto;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  margin-bottom: 1rem;
  background-color: #fff;
}

.theme--dark .segment-list {
  border-color: #2d3748;
  background-color: #1a202c;
}

.segment-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #e2e8f0;
  cursor: pointer;
  transition: background 0.2s ease;
}

.theme--dark .segment-item {
  border-bottom-color: #2d3748;
}

.segment-item:last-child {
  border-bottom: none;
}

.segment-item:hover {
  background: #f7fafc;
}

.theme--dark .segment-item:hover {
  background: #2d3748;
}

.segment-item.active {
  background: #ebf8ff;
}

.theme--dark .segment-item.active {
  background: #2c5282;
}

.segment-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
}

.segment-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: #e2e8f0;
  color: #4a5568;
  border-radius: 50%;
  font-size: 0.8rem;
  font-weight: bold;
}

.theme--dark .segment-number {
  background: #2d3748;
  color: #e2e8f0;
}

.segment-item.active .segment-number {
  background: #3182ce;
  color: white;
}

.segment-speaker {
  font-weight: 500;
  font-size: 0.85rem;
  color: #2d3748;
}

.theme--dark .segment-speaker {
  color: #e2e8f0;
}

.segment-preview {
  color: #718096;
  font-size: 0.75rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}

.theme--dark .segment-preview {
  color: #a0aec0;
}

.document-badges {
  display: flex;
  gap: 0.25rem;
}

.document-counter {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 8px;
  background: #48bb78;
  color: white;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: bold;
  box-shadow: 0 2px 4px rgba(72, 187, 120, 0.3);
  transition: all 0.2s ease;
}

.theme--dark .document-counter {
  background: #38a169;
}

.segment-item:hover .document-counter {
  transform: scale(1.1);
}

.empty-documents-message {
  padding: 20px;
  background: #f7fafc;
  border: 1px dashed #e2e8f0;
  border-radius: 8px;
  color: #4a5568;
  font-style: italic;
  text-align: center;
  position: relative;
  margin-top: 20px;
}

.theme--dark .empty-documents-message {
  background: #2d3748;
  border-color: #4a5568;
  color: #a0aec0;
}

.empty-documents-message::after {
  content: '↑';
  position: absolute;
  top: -30px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 24px;
  color: #e53e3e;
  animation: pulse-arrow 1.5s infinite;
}

@keyframes pulse-arrow {
  0%, 100% { transform: translateX(-50%) translateY(0); opacity: 0.7; }
  50% { transform: translateX(-50%) translateY(-10px); opacity: 1; }
}

/* Document Selector Styles */
.document-selector {
  margin-top: 1.5rem;
}

.selector-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.selector-header h4 {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 500;
  color: #2d3748;
}

.theme--dark .selector-header h4 {
  color: #e2e8f0;
}

/* NEW - Document Filter Controls */
.document-filter-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 1rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.theme--dark .document-filter-controls {
  border-bottom-color: rgba(255, 255, 255, 0.05);
}

.filter-title {
  font-size: 0.85rem;
  font-weight: 500;
  color: #4a5568;
}

.theme--dark .filter-title {
  color: #a0aec0;
}

.filter-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.filter-btn {
  padding: 6px 12px;
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  font-size: 0.8rem;
  color: #4a5568;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
}

.theme--dark .filter-btn {
  background: #2d3748;
  border-color: #4a5568;
  color: #a0aec0;
}

.filter-btn:hover {
  background: #edf2f7;
  transform: translateY(-1px);
}

.theme--dark .filter-btn:hover {
  background: #4a5568;
}

.filter-btn.active {
  background: rgba(4, 255, 134, 0.1);
  border-color: rgba(4, 255, 134, 0.3);
  color: rgba(4, 255, 134, 0.9);
  font-weight: 500;
}

.theme--dark .filter-btn.active {
  background: rgba(4, 255, 134, 0.2);
  color: rgba(4, 255, 134, 1);
}

.sort-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sort-label {
  font-size: 0.85rem;
  color: #4a5568;
}

.theme--dark .sort-label {
  color: #a0aec0;
}

.sort-select {
  padding: 6px 8px;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  font-size: 0.8rem;
  background: #f7fafc;
  color: #4a5568;
  cursor: pointer;
}

.theme--dark .sort-select {
  background: #2d3748;
  border-color: #4a5568;
  color: #a0aec0;
}

.search-box {
  position: relative;
  margin-left: auto;
}

.search-input {
  padding: 6px 12px 6px 32px;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  font-size: 0.8rem;
  width: 180px;
  transition: all 0.2s ease;
  background: #f7fafc;
}

.theme--dark .search-input {
  background: #2d3748;
  border-color: #4a5568;
  color: #e2e8f0;
}

.search-input:focus {
  outline: none;
  border-color: rgba(4, 255, 134, 0.4);
  box-shadow: 0 0 0 2px rgba(4, 255, 134, 0.1);
  width: 220px;
}

.search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #a0aec0;
  pointer-events: none;
}

.theme--dark .search-icon {
  color: #718096;
}

/* No Results Message */
.no-results {
  text-align: center;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.03);
  border-radius: 8px;
  margin: 1rem 0;
  color: #718096;
  font-size: 0.875rem;
}

.theme--dark .no-results {
  background: rgba(255, 255, 255, 0.05);
  color: #a0aec0;
}

.reset-filter-btn {
  background: none;
  border: none;
  color: rgba(4, 255, 134, 0.8);
  font-weight: 500;
  cursor: pointer;
  text-decoration: underline;
  padding: 4px 8px;
  margin-top: 8px;
  font-size: 0.85rem;
}

.reset-filter-btn:hover {
  color: rgba(4, 255, 134, 1);
}

/* Carousel Styles */
.carousel-container {
  position: relative;
  display: flex;
  align-items: center;
  padding: 0;
  cursor: pointer;
}

.carousel-track {
  display: flex;
  overflow-x: auto;
  scroll-behavior: smooth;
  padding: 0.5rem;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  gap: 16px;
}

.carousel-track::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.carousel-item {
  flex: 0 0 auto;
  width: 220px;
  height: 180px;
  border-radius: 8px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.carousel-item.mapped::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #48bb78 0%, #38a169 100%);
  animation: gradientSlide 1.5s linear infinite;
}

.upload-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.2s ease;
  position: relative;
}

.theme--dark .upload-card {
  background: #2d3748;
  border-color: #4a5568;
}

.carousel-item.mapped .upload-card {
  border-color: #48bb78;
  background: #f0fff4;
}

.theme--dark .carousel-item.mapped .upload-card {
  border-color: #38a169;
  background: #2d3748;
}

.upload-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
  border-color: #3182ce;
}

.upload-image {
  height: 65%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e2e8f0;
}

.theme--dark .upload-image {
  background: #4a5568;
}

.upload-image img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: transform 0.3s ease;
}

.upload-card:hover .upload-image img {
  transform: scale(1.05);
}

/* Video thumbnail */
.video-thumbnail {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-icon {
  position: absolute;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(233, 30, 99, 0.8);
  color: white;
  border-radius: 50%;
  font-size: 1.2rem;
  z-index: 5;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.video-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: rgba(233, 30, 99, 0.1);
  color: rgba(233, 30, 99, 0.8);
  font-size: 1.2rem;
  font-weight: bold;
}

.preview-video {
  max-width: 100%;
  max-height: 70vh;
  border-radius: 4px;
}

.upload-info {
  padding: 10px;
  height: 35%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.upload-title {
  font-weight: 500;
  font-size: 0.85rem;
  line-height: 1.2;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  margin-bottom: 4px;
  color: #2d3748;
}

.theme--dark .upload-title {
  color: #e2e8f0;
}

.upload-meta {
  font-size: 0.7rem;
  color: #718096;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.theme--dark .upload-meta {
  color: #a0aec0;
}

/* Mapping indicator */
.mapping-indicator {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #e2e8f0;
  color: #4a5568;
  font-size: 16px;
  font-weight: bold;
  transition: all 0.2s ease;
}

.theme--dark .mapping-indicator {
  background: #4a5568;
  color: #e2e8f0;
}

.mapping-indicator.is-mapped {
  background: #48bb78;
  color: white;
}

.theme--dark .mapping-indicator.is-mapped {
  background: #38a169;
}

/* Media type badges */
.media-type-badge {
  position: absolute;
  bottom: 8px;
  right: 8px;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 500;
  z-index: 5;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.video-badge {
  background: #E91E63;
  color: white;
}

.image-badge {
  background: #2196F3;
  color: white;
}

.doc-badge {
  background: #FF9800;
  color: white;
}

/* Scroll Arrows */
.scroll-arrow {
  position: absolute;
  width: 40px;
  height: 40px;
  background: #e2e8f0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.2s ease;
}

.theme--dark .scroll-arrow {
  background: #4a5568;
}

.scroll-arrow:hover {
  background: #cbd5e0;
}

.theme--dark .scroll-arrow:hover {
  background: #718096;
}

.scroll-arrow.left {
  left: 10px;
}

.scroll-arrow.right {
  right: 10px;
}

.scroll-arrow span {
  color: #2d3748;
  font-size: 0.9rem;
}

.theme--dark .scroll-arrow span {
  color: #e2e8f0;
}

/* Preview Modal */
.preview-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.preview-content {
  width: 95%;
  height: 95%;
  max-width: 1600px;
  max-height: 95vh;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.theme--dark .preview-content {
  background: #1a202c;
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f7fafc;
  border-bottom: 1px solid #e2e8f0;
}

.theme--dark .preview-header {
  background: #2d3748;
  border-bottom-color: #4a5568;
}

.preview-header h3 {
  margin: 0;
  font-size: 1.1rem;
  color: #2d3748;
  font-weight: 500;
}

.theme--dark .preview-header h3 {
  color: #e2e8f0;
}

.close-preview {
  background: none;
  border: none;
  color: #718096;
  font-size: 24px;
  cursor: pointer;
}

.theme--dark .close-preview {
  color: #a0aec0;
}

.preview-body {
  flex: 1;
  overflow: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: #f7fafc;
}

.theme--dark .preview-body {
  background: #1a202c;
}

.preview-body img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.preview-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 20px;
}

.preview-placeholder p {
  color: #718096;
  font-size: 1.1rem;
}

.theme--dark .preview-placeholder p {
  color: #a0aec0;
}

/* File Icon Styles */
.file-icon {
  width: 80px;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.2rem;
  color: white;
  position: relative;
}

.file-icon:before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #e0e0e0;
  clip-path: polygon(0% 0%, 80% 0%, 100% 20%, 100% 100%, 0% 100%);
}

.file-icon:after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 20px;
  height: 20px;
  background: #c0c0c0;
  clip-path: polygon(0% 0%, 100% 0%, 100% 100%);
}

.file-icon.large {
  width: 160px;
  height: 200px;
  font-size: 2rem;
}

.pdf-icon:before { background: #FF5722; }
.doc-icon:before { background: #2196F3; }
.xls-icon:before { background: #4CAF50; }
.ppt-icon:before { background: #FF9800; }
.video-icon:before { background: #E91E63; }
.generic-icon:before { background: #9E9E9E; }

/* Tooltip for card action */
.card-tooltip {
  position: absolute;
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%) translateY(20px);
  background: #3182ce;
  color: white;
  font-size: 0.7rem;
  padding: 4px 10px;
  border-radius: 12px;
  opacity: 0;
  transition: all 0.3s ease;
  pointer-events: none;
  white-space: nowrap;
}

.upload-card:hover .card-tooltip {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}

.carousel-item.mapped .card-tooltip {
  background: #48bb78;
}

/* Clear selection button */
.clear-selection-btn {
  background: #feb2b2;
  border: 1px solid #fc8181;
  border-radius: 4px;
  padding: 6px 12px;
  color: #e53e3e;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.theme--dark .clear-selection-btn {
  background: #742a2a;
  border-color: #fc8181;
  color: #feb2b2;
}

.clear-selection-btn:hover {
  background: #fc8181;
  color: white;
  transform: scale(1.05);
}

.theme--dark .clear-selection-btn:hover {
  background: #e53e3e;
  color: white;
}

.clear-selection-btn:active {
  transform: scale(0.95);
}

/* Highlight effect for newly uploaded documents */
.carousel-item.highlight-new {
  animation: highlightPulse 3.5s ease-out;
}

@keyframes highlightPulse {
  0% {
    box-shadow: 0 0 0 0 rgba(49, 130, 206, 0.7);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 0 15px 5px rgba(49, 130, 206, 0.3);
    transform: scale(1.02);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(49, 130, 206, 0);
    transform: scale(1);
  }
}

/* Media error message */
.media-error-message {
  color: #e53e3e;
  font-size: 0.75rem;
  text-align: center;
  margin-top: 0.5rem;
  background: rgba(229, 62, 62, 0.1);
  padding: 4px 8px;
  border-radius: 4px;
}

/* Animations */
@keyframes gradientSlide {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Mobile Responsive Design */
@media screen and (max-width: 768px) {
  .carousel-item {
    width: 180px;
    height: 160px;
  }

  .search-box {
    width: 100%;
    margin-top: 8px;
    margin-left: 0;
  }

  .search-input {
    width: 100%;
  }

  .segment-filter-controls,
  .document-filter-controls {
    flex-direction: column;
    align-items: flex-start;
  }

  .filter-buttons,
  .segment-filter-buttons {
    width: 100%;
    justify-content: space-between;
  }

  .sort-controls {
    width: 100%;
    margin-top: 8px;
  }

  .scroll-arrow {
    width: 32px;
    height: 32px;
  }

  .upload-title {
    font-size: 0.8rem;
  }

  .preview-content {
    width: 95%;
    height: 80%;
  }
}

/* Highlight effect for upload button */
.highlight-upload {
  animation: uploadPulse 3s ease-out;
}

@keyframes uploadPulse {
  0% {
    box-shadow: 0 0 0 0 rgba(229, 62, 62, 0.7);
  }
  50% {
    box-shadow: 0 0 20px 10px rgba(229, 62, 62, 0.3);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(229, 62, 62, 0);
  }
}
</style>
